import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import {
  Upload,
  Search,
  Mail,
  Phone,
  MapPin,
  Building2,
  Scissors,
  Users,
  Send,
  Trash2,
  Download,
  Filter,
  CheckSquare,
  Square,
  Loader2,
  X,
  FileSpreadsheet,
} from "lucide-react";

const API = window.location.origin + "/api";

const REGIONI_ITALIANE = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
  "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto"
];

const ContattiMarketingPage = () => {
  const [contatti, setContatti] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRegione, setSelectedRegione] = useState("");
  const [selectedCategoria, setSelectedCategoria] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContatti, setSelectedContatti] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  
  const [importForm, setImportForm] = useState({
    file: null,
    regione: "",
    categoria: ""
  });
  
  const [sendForm, setSendForm] = useState({
    canale: "email",
    oggetto_email: "",
    messaggio_email: "",
    messaggio_whatsapp: "",
    invio_scaglionato: true,
    messaggi_per_ora: 50
  });
  
  const [whatsappLinksDialogOpen, setWhatsappLinksDialogOpen] = useState(false);
  const [whatsappLinks, setWhatsappLinks] = useState([]);
  const [currentLinkIndex, setCurrentLinkIndex] = useState(0);

  const fetchContatti = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (selectedRegione) params.append("regione", selectedRegione);
      if (selectedCategoria) params.append("categoria", selectedCategoria);
      if (searchQuery) params.append("search", searchQuery);
      
      const res = await axios.get(`${API}/admin/contatti-marketing?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContatti(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento contatti");
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/contatti-marketing/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchContatti();
  }, [selectedRegione, selectedCategoria, searchQuery]);

  const handleImport = async () => {
    if (!importForm.file || !importForm.regione || !importForm.categoria) {
      toast.error("Compila tutti i campi");
      return;
    }
    
    setImporting(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", importForm.file);
      formData.append("regione", importForm.regione);
      formData.append("categoria", importForm.categoria);
      
      const res = await axios.post(`${API}/admin/contatti-marketing/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      toast.success(`Import completato: ${res.data.inseriti} contatti inseriti, ${res.data.scartati_duplicati} duplicati, ${res.data.scartati_invalidi} invalidi`);
      setImportDialogOpen(false);
      setImportForm({ file: null, regione: "", categoria: "" });
      fetchContatti();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'import");
    } finally {
      setImporting(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedContatti.length === contatti.length) {
      setSelectedContatti([]);
    } else {
      setSelectedContatti(contatti.map(c => c.contatto_id));
    }
  };

  const handleSelectContatto = (contattoId) => {
    if (selectedContatti.includes(contattoId)) {
      setSelectedContatti(selectedContatti.filter(id => id !== contattoId));
    } else {
      setSelectedContatti([...selectedContatti, contattoId]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedContatti.length === 0) return;
    
    if (!window.confirm(`Eliminare ${selectedContatti.length} contatti selezionati?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/contatti-marketing/bulk/delete`, {
        headers: { Authorization: `Bearer ${token}` },
        data: selectedContatti
      });
      toast.success(`Eliminati ${selectedContatti.length} contatti`);
      setSelectedContatti([]);
      fetchContatti();
      fetchStats();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const handleSend = async () => {
    if (selectedContatti.length === 0) {
      toast.error("Seleziona almeno un destinatario");
      return;
    }
    
    if (sendForm.canale === "email" && !sendForm.messaggio_email) {
      toast.error("Inserisci il messaggio email");
      return;
    }
    
    if (sendForm.canale === "whatsapp" && !sendForm.messaggio_whatsapp) {
      toast.error("Inserisci il messaggio WhatsApp");
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      
      // Create campaign with throttling settings
      const campagnaRes = await axios.post(`${API}/admin/campagne-marketing`, {
        titolo: `Campagna ${new Date().toLocaleDateString()}`,
        messaggio_email: sendForm.messaggio_email,
        oggetto_email: sendForm.oggetto_email,
        messaggio_whatsapp: sendForm.messaggio_whatsapp,
        destinatari: selectedContatti,
        canale: sendForm.canale,
        invio_scaglionato: sendForm.invio_scaglionato,
        messaggi_per_ora: sendForm.messaggi_per_ora
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Send campaign
      const invioRes = await axios.post(`${API}/admin/campagne-marketing/${campagnaRes.data.campagna_id}/invia`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (sendForm.canale === "whatsapp" || sendForm.canale === "entrambi") {
        // Show WhatsApp links in a modal for controlled sending
        if (invioRes.data.whatsapp_links?.length > 0) {
          setWhatsappLinks(invioRes.data.whatsapp_links);
          setCurrentLinkIndex(0);
          setWhatsappLinksDialogOpen(true);
          toast.success(`${invioRes.data.whatsapp_links.length} link WhatsApp pronti per l'invio`);
        }
      }
      
      if (sendForm.canale === "email" || sendForm.canale === "entrambi") {
        toast.success(`${invioRes.data.inviati} email inviate, ${invioRes.data.falliti} fallite`);
      }
      
      setSendDialogOpen(false);
      setSendForm({ 
        canale: "email", 
        oggetto_email: "", 
        messaggio_email: "", 
        messaggio_whatsapp: "",
        invio_scaglionato: true,
        messaggi_per_ora: 50
      });
      setSelectedContatti([]);
    } catch (error) {
      toast.error("Errore durante l'invio");
    } finally {
      setSending(false);
    }
  };

  const handleOpenNextWhatsApp = () => {
    if (currentLinkIndex < whatsappLinks.length) {
      window.open(whatsappLinks[currentLinkIndex].link, '_blank');
      setCurrentLinkIndex(prev => prev + 1);
    }
  };

  const handleOpenMultipleWhatsApp = (count) => {
    const endIndex = Math.min(currentLinkIndex + count, whatsappLinks.length);
    for (let i = currentLinkIndex; i < endIndex; i++) {
      setTimeout(() => window.open(whatsappLinks[i].link, '_blank'), (i - currentLinkIndex) * 2000);
    }
    setCurrentLinkIndex(endIndex);
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (selectedRegione) params.append("regione", selectedRegione);
      if (selectedCategoria) params.append("categoria", selectedCategoria);
      
      const res = await axios.post(`${API}/admin/contatti-marketing/export?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contatti_${selectedRegione || 'tutte'}_${selectedCategoria || 'tutte'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Errore durante l'export");
    }
  };

  return (
    <PageLayout title="Database Marketing" subtitle="Gestione contatti centri estetici e parrucchieri">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto text-purple-600 mb-2" />
            <p className="text-2xl font-bold text-purple-700">{stats.totale || 0}</p>
            <p className="text-xs text-purple-600">Totale Contatti</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
          <CardContent className="p-4 text-center">
            <Building2 className="w-8 h-8 mx-auto text-pink-600 mb-2" />
            <p className="text-2xl font-bold text-pink-700">{stats.centri_estetici || 0}</p>
            <p className="text-xs text-pink-600">Centri Estetici</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4 text-center">
            <Scissors className="w-8 h-8 mx-auto text-blue-600 mb-2" />
            <p className="text-2xl font-bold text-blue-700">{stats.parrucchieri || 0}</p>
            <p className="text-xs text-blue-600">Parrucchieri</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4 text-center">
            <CheckSquare className="w-8 h-8 mx-auto text-green-600 mb-2" />
            <p className="text-2xl font-bold text-green-700">{selectedContatti.length}</p>
            <p className="text-xs text-green-600">Selezionati</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Actions */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-gray-500 mb-1 block">Regione</Label>
              <Select value={selectedRegione || "all"} onValueChange={(v) => setSelectedRegione(v === "all" ? "" : v)}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Tutte le regioni" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le regioni</SelectItem>
                  {REGIONI_ITALIANE.map(r => (
                    <SelectItem key={r} value={r}>
                      {r} {stats.per_regione?.[r] ? `(${stats.per_regione[r]})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-48">
              <Label className="text-xs text-gray-500 mb-1 block">Categoria</Label>
              <Select value={selectedCategoria || "all"} onValueChange={(v) => setSelectedCategoria(v === "all" ? "" : v)}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="centro_estetico">🟣 Centri Estetici</SelectItem>
                  <SelectItem value="parrucchiere">🔵 Parrucchieri</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-gray-500 mb-1 block">Cerca</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca per nome o città..."
                  className="pl-10 rounded-xl"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => setImportDialogOpen(true)}
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={handleExport}
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selection Actions */}
      {selectedContatti.length > 0 && (
        <Card className="mb-4 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="p-4 flex items-center justify-between">
            <span className="font-medium text-purple-700">
              {selectedContatti.length} contatti selezionati
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl border-red-300 text-red-600 hover:bg-red-50"
                onClick={handleDeleteSelected}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina
              </Button>
              <Button
                size="sm"
                className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-600"
                onClick={() => setSendDialogOpen(true)}
              >
                <Send className="w-4 h-4 mr-2" />
                Invia Comunicazione
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contacts Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              Elenco Contatti ({contatti.length})
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              className="text-purple-600"
            >
              {selectedContatti.length === contatti.length ? (
                <>
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Deseleziona tutto
                </>
              ) : (
                <>
                  <Square className="w-4 h-4 mr-2" />
                  Seleziona tutto
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-y">
                <tr>
                  <th className="p-2 text-left w-10"></th>
                  <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Nome Centro</th>
                  <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Città</th>
                  <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Regione</th>
                  <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Cat.</th>
                  <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Cellulare</th>
                  <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                </tr>
              </thead>
              <tbody>
                {contatti.map((contatto) => (
                  <tr 
                    key={contatto.contatto_id} 
                    className={`border-b hover:bg-gray-50 cursor-pointer ${
                      selectedContatti.includes(contatto.contatto_id) ? 'bg-purple-50' : ''
                    }`}
                    onClick={() => handleSelectContatto(contatto.contatto_id)}
                  >
                    <td className="p-2">
                      <Checkbox
                        checked={selectedContatti.includes(contatto.contatto_id)}
                        onCheckedChange={() => handleSelectContatto(contatto.contatto_id)}
                      />
                    </td>
                    <td className="p-2 text-xs font-medium max-w-[180px] truncate" title={contatto.nome_centro}>
                      {contatto.nome_centro}
                    </td>
                    <td className="p-2 text-xs text-gray-600 max-w-[100px] truncate" title={contatto.citta}>
                      {contatto.citta}
                    </td>
                    <td className="p-2 text-xs text-gray-500 max-w-[90px] truncate" title={contatto.regione}>
                      {contatto.regione}
                    </td>
                    <td className="p-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${
                        contatto.categoria === 'centro_estetico' 
                          ? 'bg-purple-500' 
                          : 'bg-blue-500'
                      }`} title={contatto.categoria === 'centro_estetico' ? 'Centro Estetico' : 'Parrucchiere'}></span>
                    </td>
                    <td className="p-2 text-xs">
                      {contatto.cellulare && (
                        <a 
                          href={`tel:${contatto.cellulare}`}
                          className="text-green-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {contatto.cellulare}
                        </a>
                      )}
                    </td>
                    <td className="p-2 text-xs max-w-[180px] truncate">
                      {contatto.email && (
                        <a 
                          href={`mailto:${contatto.email}`}
                          className="text-blue-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                          title={contatto.email}
                        >
                          {contatto.email}
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
                {contatti.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-gray-500">
                      Nessun contatto trovato. Importa un file per iniziare.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Importa Contatti da File</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Regione *</Label>
              <Select value={importForm.regione} onValueChange={(v) => setImportForm({...importForm, regione: v})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Seleziona regione" />
                </SelectTrigger>
                <SelectContent>
                  {REGIONI_ITALIANE.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <Select value={importForm.categoria} onValueChange={(v) => setImportForm({...importForm, categoria: v})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Seleziona categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="centro_estetico">🟣 Centro Estetico</SelectItem>
                  <SelectItem value="parrucchiere">🔵 Parrucchiere</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>File Excel/CSV *</Label>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setImportForm({...importForm, file: e.target.files[0]})}
              />
              {importForm.file ? (
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
                  <FileSpreadsheet className="w-5 h-5 text-green-600" />
                  <span className="flex-1 truncate">{importForm.file.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setImportForm({...importForm, file: null})}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full rounded-xl h-20 border-dashed"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="flex flex-col items-center">
                    <Upload className="w-6 h-6 mb-2 text-gray-400" />
                    <span>Clicca per selezionare file</span>
                    <span className="text-xs text-gray-400">.csv, .xlsx, .xls</span>
                  </div>
                </Button>
              )}
            </div>
            
            <p className="text-xs text-gray-500">
              Il sistema cercherà automaticamente le colonne: nome/ragione sociale, città, cellulare, email.
              I duplicati (stesso cellulare o email) verranno scartati.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)} className="rounded-full">
              Annulla
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={importing}
              className="rounded-full bg-gradient-to-r from-purple-600 to-pink-600"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Importa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="rounded-2xl max-w-xl">
          <DialogHeader>
            <DialogTitle>Invia Comunicazione a {selectedContatti.length} destinatari</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Canale di invio</Label>
              <Select value={sendForm.canale} onValueChange={(v) => setSendForm({...sendForm, canale: v})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">📧 Solo Email</SelectItem>
                  <SelectItem value="whatsapp">📱 Solo WhatsApp</SelectItem>
                  <SelectItem value="entrambi">📧📱 Entrambi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(sendForm.canale === "email" || sendForm.canale === "entrambi") && (
              <>
                <div className="space-y-2">
                  <Label>Oggetto Email *</Label>
                  <Input
                    value={sendForm.oggetto_email}
                    onChange={(e) => setSendForm({...sendForm, oggetto_email: e.target.value})}
                    placeholder="Oggetto della mail"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Messaggio Email *</Label>
                  <Textarea
                    value={sendForm.messaggio_email}
                    onChange={(e) => setSendForm({...sendForm, messaggio_email: e.target.value})}
                    placeholder="Scrivi il messaggio email..."
                    className="rounded-xl min-h-[120px]"
                  />
                </div>
              </>
            )}
            
            {(sendForm.canale === "whatsapp" || sendForm.canale === "entrambi") && (
              <div className="space-y-2">
                <Label>Messaggio WhatsApp *</Label>
                <Textarea
                  value={sendForm.messaggio_whatsapp}
                  onChange={(e) => setSendForm({...sendForm, messaggio_whatsapp: e.target.value})}
                  placeholder="Scrivi il messaggio WhatsApp... Usa {nome} per inserire il nome del centro e {citta} per la città"
                  className="rounded-xl min-h-[120px]"
                />
                <p className="text-xs text-gray-500">
                  Variabili disponibili: {"{nome}"} = nome centro, {"{citta}"} = città
                </p>
              </div>
            )}
            
            {/* Throttling Options for Email */}
            {(sendForm.canale === "email" || sendForm.canale === "entrambi") && (
              <div className="space-y-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="throttle"
                    checked={sendForm.invio_scaglionato}
                    onCheckedChange={(checked) => setSendForm({...sendForm, invio_scaglionato: checked})}
                  />
                  <Label htmlFor="throttle" className="text-sm font-medium text-amber-800">
                    Invio scaglionato (evita blocchi)
                  </Label>
                </div>
                {sendForm.invio_scaglionato && (
                  <div className="pl-6">
                    <Label className="text-xs text-amber-700">Email per batch</Label>
                    <Select 
                      value={String(sendForm.messaggi_per_ora)} 
                      onValueChange={(v) => setSendForm({...sendForm, messaggi_per_ora: parseInt(v)})}
                    >
                      <SelectTrigger className="rounded-xl mt-1 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25 email, poi pausa 1 min</SelectItem>
                        <SelectItem value="50">50 email, poi pausa 1 min</SelectItem>
                        <SelectItem value="100">100 email, poi pausa 1 min</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            
            {/* WhatsApp Warning */}
            {(sendForm.canale === "whatsapp" || sendForm.canale === "entrambi") && (
              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <p className="text-sm text-green-800 font-medium mb-1">📱 Modalità WhatsApp sicura</p>
                <p className="text-xs text-green-700">
                  I link WhatsApp verranno aperti uno alla volta in una finestra controllata. 
                  Attendi 30-60 secondi tra un invio e l'altro per evitare blocchi da Meta.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)} className="rounded-full">
              Annulla
            </Button>
            <Button 
              onClick={handleSend} 
              disabled={sending}
              className="rounded-full bg-gradient-to-r from-purple-600 to-pink-600"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Links Dialog */}
      <Dialog open={whatsappLinksDialogOpen} onOpenChange={setWhatsappLinksDialogOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-green-600" />
              Invio WhatsApp Controllato
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {/* Progress */}
            <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-800">Progresso invio</span>
                <span className="text-lg font-bold text-green-700">{currentLinkIndex} / {whatsappLinks.length}</span>
              </div>
              <div className="w-full bg-green-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all"
                  style={{ width: `${(currentLinkIndex / whatsappLinks.length) * 100}%` }}
                />
              </div>
            </div>
            
            {/* Current contact */}
            {currentLinkIndex < whatsappLinks.length && (
              <div className="mb-4 p-4 bg-white rounded-xl border-2 border-green-300 shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Prossimo destinatario:</p>
                <p className="font-bold text-lg">{whatsappLinks[currentLinkIndex]?.nome}</p>
                <p className="text-sm text-gray-600">{whatsappLinks[currentLinkIndex]?.citta}</p>
                <p className="text-sm text-green-600">{whatsappLinks[currentLinkIndex]?.cellulare}</p>
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex flex-col gap-2 mb-4">
              <Button
                onClick={handleOpenNextWhatsApp}
                disabled={currentLinkIndex >= whatsappLinks.length}
                className="w-full rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 h-12"
              >
                <Send className="w-5 h-5 mr-2" />
                Apri WhatsApp per {whatsappLinks[currentLinkIndex]?.nome || 'prossimo'}
              </Button>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleOpenMultipleWhatsApp(5)}
                  disabled={currentLinkIndex >= whatsappLinks.length}
                  className="flex-1 rounded-xl border-green-300 text-green-700 hover:bg-green-50"
                >
                  Apri prossimi 5
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleOpenMultipleWhatsApp(10)}
                  disabled={currentLinkIndex >= whatsappLinks.length}
                  className="flex-1 rounded-xl border-green-300 text-green-700 hover:bg-green-50"
                >
                  Apri prossimi 10
                </Button>
              </div>
            </div>
            
            {/* Warning */}
            <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 mb-4">
              <p className="text-xs text-amber-800">
                ⚠️ <strong>Attenzione:</strong> Per evitare blocchi da Meta, attendi almeno 30-60 secondi tra un invio e l'altro. 
                Non aprire più di 50-100 link in un'ora.
              </p>
            </div>
            
            {/* Link list */}
            <div className="max-h-[200px] overflow-y-auto space-y-1">
              {whatsappLinks.map((link, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                    idx < currentLinkIndex ? 'bg-green-100 text-green-800' : 
                    idx === currentLinkIndex ? 'bg-green-200 border border-green-400' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-xs font-bold">
                      {idx + 1}
                    </span>
                    <span className="font-medium truncate max-w-[200px]">{link.nome}</span>
                    <span className="text-gray-500 text-xs">{link.citta}</span>
                  </div>
                  {idx < currentLinkIndex ? (
                    <span className="text-green-600 text-xs">✓ Aperto</span>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(link.link, '_blank')}
                      className="h-6 px-2 text-xs"
                    >
                      Apri
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setWhatsappLinksDialogOpen(false);
                setWhatsappLinks([]);
                setCurrentLinkIndex(0);
              }} 
              className="rounded-full"
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default ContattiMarketingPage;
